import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { updateNotifications } from '@/data/notifications';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';

/**
 * Scope matching for notification mark-read / badge counts.
 * Board and task notifications don't reliably carry their IDs in `parameters`
 * (reachchms-functions send-notifications parses boardID from `path`; TASK_ASSIGNED
 * has no parameters at all), so fall back to path parsing for those scopes.
 *
 * A notification matches when its type is in `types` (if provided) AND it matches
 * at least one provided ID filter. With no ID filters, any type match counts.
 */
export const notificationMatchesScope = (n, { conversationID, boardID, postID, taskInstanceID, types } = {}) => {
    if (types?.length && !types.includes(n.notificationType)) return false;

    const hasIDFilter = conversationID || boardID || postID || taskInstanceID;
    if (!hasIDFilter) return true;

    if (conversationID && n.parameters?.conversationID == conversationID) return true;
    if (boardID && (n.parameters?.boardID == boardID || n.path?.match(new RegExp(`connect/boards/${boardID}/`)))) return true;
    if (postID && n.parameters?.postID == postID) return true;
    if (taskInstanceID && (n.parameters?.taskInstanceID == taskInstanceID || n.path?.match(new RegExp(`tasks/instances/${taskInstanceID}$`)))) return true;

    return false;
};

/**
 * Marks unread notifications matching `scope` as read when content is viewed.
 * Optimistically updates the ['loggedInUser'] cache so badges clear instantly,
 * and only invalidates on error (optimistic state is truth — invalidation flickers).
 *
 * @param {Object} scope — { conversationID?, boardID?, postID?, taskInstanceID?, types? }
 *   types = array of notificationType strings; omit = all types matching the ID filters.
 */
const useMarkNotificationsRead = (scope = {}) => {
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    // IDs that have been sent to updateNotifications — prevents duplicate API
    // calls while still allowing the optimistic update to be re-applied if a
    // background refetch overwrites the cache with stale isRead:false.
    const apiSubmittedIdsRef = useRef(new Set());

    // Serialize so the effect re-runs on scope value changes, not object identity
    const scopeKey = JSON.stringify(scope);

    useEffect(() => {
        const parsedScope = JSON.parse(scopeKey);
        const unread = (loggedInUser?.Notifications || []).filter(n =>
            !n.isRead && n.id && notificationMatchesScope(n, parsedScope)
        );
        if (!unread.length) return;

        const matchedIds = new Set(unread.map(n => n.id));

        // Optimistic update — runs every time the effect fires, including after
        // a background refetch overwrites the cache with stale isRead:false.
        queryClient.setQueryData(QueryKeys.people.loggedInUser(), old => old ? ({
            ...old,
            Notifications: (old.Notifications || []).map(n => matchedIds.has(n.id) ? { ...n, isRead: true } : n),
        }) : old);

        // Only hit the API for IDs we haven't already submitted — avoids
        // duplicate updateNotifications calls while staying resilient to
        // refetch-overwrite (the optimistic update above always re-applies).
        const apiIds = unread.filter(n => !apiSubmittedIdsRef.current.has(n.id));
        if (apiIds.length === 0) return;

        apiIds.forEach(n => apiSubmittedIdsRef.current.add(n.id));

        updateNotifications(apiIds.map(n => ({ id: n.id, isRead: true })))
            .catch(() => {
                apiIds.forEach(n => apiSubmittedIdsRef.current.delete(n.id));
                queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
            });
    }, [loggedInUser?.Notifications, scopeKey]);
};

export default useMarkNotificationsRead;
